<script lang="ts">
import { BButton, BCollapse, BMessage, BTooltip } from "buefy";
import "@/style/components/ShowErrors.scss";

export default {
  name: "ShowErrors",
  components: { BMessage, BButton, BTooltip, BCollapse },
  props: {
    title: {
      required: false,
      type: String,
    },
    errorsMessages: {
      type: Array,
      required: false,
    },
    errorsMessagesLength: {
      type: Number,
      default: 0,
    },
    warningMessages: {
      type: Array,
      required: false,
    },
    warningMessagesLength: {
      type: Number,
      default: 0,
    },
    // Bannieres "danger" ( rouge ) - parallele a warningMessages mais
    // pour les cas non recuperables ( ex : compte ferme CLOSED_ACCOUNT
    // necessitant un contact admin , distinct des warnings reactivables ) .
    dangerMessages: {
      type: Array,
      required: false,
    },
    dangerMessagesLength: {
      type: Number,
      default: 0,
    },
    password: {
      type: String,
    },
  },
  data() {
    return {
      // Tracks which messages have been dismissed by the user. Indexed on
      // errorsMessages. A dismissal is persistent across collapse open /
      // close cycles ( fix #490 ) - the only way to bring a message back
      // is the "restore all" button below.
      dismissed: [] as boolean[],
    };
  },
  computed: {
    dismissedCount(): number {
      return this.dismissed.filter(Boolean).length;
    },
    hasDismissed(): boolean {
      return this.dismissedCount > 0;
    },
  },
  watch: {
    // Reset the dismissed array every time the errors list changes ( new
    // file uploaded , different data type , etc. ). Runs immediately to
    // initialise the array to the right length on mount.
    errorsMessages: {
      immediate: true,
      handler(val: unknown[]) {
        this.dismissed = new Array((val || []).length).fill(false);
      },
    },
  },
  methods: {
    // Called when <b-message> emits 'close' ( user clicked the X ). We
    // record the dismissal in our array ; the one-way :model-value
    // binding on b-message then carries the false state back to the
    // component on the next render , keeping Buefy's internal isActive
    // in sync with our source of truth.
    handleMessageClose(index: number): void {
      if (!this.dismissed[index]) {
        this.dismissed[index] = true;
      }
    },
    // Triggered by the "restore all" button. Flips every dismissed flag
    // back to false in one go ; Buefy's watcher on modelValue will
    // re-activate each <b-message>.
    restoreAll(): void {
      this.dismissed = this.dismissed.map(() => false);
    },
  },
};
</script>

<template>
  <div
    v-if="dangerMessagesLength !== 0 && password"
    class="cardMessageErrors"
  >
    <div
      v-for="msg in dangerMessages"
      :key="msg"
    >
      <b-message
        :aria-close-label="$t('message.close')"
        :title="$t('message.login-danger')"
        has-icon
        icon="exclamation-circle"
        type="is-danger"
      >
        <span
          class="column"
          v-html="msg"
        />
      </b-message>
    </div>
  </div>
  <div
    v-else-if="warningMessagesLength !== 0 && password"
    class="cardMessageErrors"
  >
    <div
      v-for="msg in warningMessages"
      :key="msg"
    >
      <b-message
        :aria-close-label="$t('message.close')"
        :title="$t('message.login-warning')"
        has-icon
        icon="exclamation-triangle"
        type="is-warning"
      >
        <span
          class="column"
          v-html="msg"
        />
      </b-message>
    </div>
  </div>
  <div v-else>
    <b-collapse
      :model-value="false"
      aria-id="contentIdForA11y1"
    >
      <template #trigger="props">
        <b-tooltip
          :label="$t('applications.show_errors')"
          position="is-right"
          type="is-danger"
        >
          <b-button
            v-if="errorsMessagesLength !== 0"
            :aria-expanded="props.open"
            aria-controls="contentIdForA11y1"
            class="btnErrors"
            icon-right="exclamation-circle"
            outlined
            expanded
            :tabindex="errorsMessagesLength !== 0 ? 0 : -1"
            type="is-danger"
            :aria-label="$t('applications.show_errors') + ': ' + $t('applications.number_errors', { numberErrors: errorsMessagesLength })"
          >
            {{ $t("applications.number_errors", { numberErrors: errorsMessagesLength }) }}
          </b-button>
        </b-tooltip>
      </template>
      <div class="notification">
        <div class="content">
          <div
            v-if="hasDismissed"
            class="restoreErrorsBar"
          >
            <b-tooltip
              :label="$t('applications.restore_errors_count', { count: dismissedCount })"
              position="is-left"
              type="is-dark"
            >
              <b-button
                icon-left="eye"
                size="is-small"
                type="is-danger"
                outlined
                :aria-label="$t('applications.restore_errors')"
                @click="restoreAll"
              >
                {{ $t("applications.restore_errors") }}
              </b-button>
            </b-tooltip>
          </div>
          <div
            v-for="(msg, keys) in errorsMessages"
            :key="keys"
            class="cardMessageErrors"
          >
            <b-message
              :title="title ? title : $t('message.app-config-error') + ' : ' + msg.title"
              class="mt-4"
              has-icon
              type="is-danger"
              aria-close-label="Close message"
              :model-value="!dismissed[keys]"
              @close="handleMessageClose(keys)"
            >
              <span
                v-if="msg?.params"
                class="sizeWidth"
              >
                <span
                  v-if="msg.params.path"
                  class="columns"
                >
                  <p :aria-label="$t('errors-yaml.path')">
                    {{ $t("errors-yaml.path") }}
                    <code :aria-label="msg.params.path">
                      <b>{{ msg.params.path }}</b>
                    </code>
                  </p>
                </span>
                <span
                  v-if="msg.message"
                  class="columns"
                  :aria-label="msg.message"
                  v-html="msg.message"
                />
              </span>
              <span
                v-else
                :aria-label="msg"
                v-html="msg"
              />
            </b-message>
          </div>
        </div>
      </div>
    </b-collapse>
  </div>
</template>
